/**
 * Mini cart drawer (sections/mini-cart.liquid).
 *
 * The drawer itself is the theme <cart-drawer> element (theme.js); quantity changes live in
 * component-cart-quantity.js and the dispatch countdown in component-market-usps.js.
 *
 * This file:
 * - clears the theme's "open the drawer after every add to cart" flag, a job the added-to-cart
 *   popup (component-added-to-cart-popup.js) took over;
 * - mirrors the open state on <html> (class "mini-cart-open") for the push layout CSS, and keeps
 *   the drawer open from page to page on tablet and up: the open state is stored in
 *   sessionStorage, the section's inline script flags <html> before paint and the drawer is
 *   restored here without transition ("mini-cart-restoring");
 * - lets the customer keep browsing with the drawer open on tablet and up: the theme focus trap
 *   (which closes the drawer on any click outside) is disabled, Escape still closes it.
 */
(() => {
  const STORAGE_KEY = 'mini-cart:open';
  const OPEN_CLASS = 'mini-cart-open';
  const RESTORING_CLASS = 'mini-cart-restoring';
  const persistMedia = window.matchMedia('(min-width: 700px)');

  const storage = {
    get() {
      try {
        return sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch (error) {
        return false;
      }
    },
    set(isOpen) {
      try {
        if (isOpen) {
          sessionStorage.setItem(STORAGE_KEY, '1');
        } else {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        // Storage unavailable: the drawer just does not persist.
      }
    }
  };

  document.addEventListener('cart:refresh', (event) => {
    if (event.detail && event.detail.openMiniCart) {
      event.detail.openMiniCart = false;
    }
  }, true);

  document.addEventListener('openable-element:open', (event) => {
    if (event.target && event.target.id === 'mini-cart') {
      document.documentElement.classList.add(OPEN_CLASS);
      storage.set(persistMedia.matches);

      // The header search drawer shares the right edge (component-search-drawer.js closes the
      // mini cart in the other direction).
      const searchDrawer = document.getElementById('search-drawer');

      if (searchDrawer && searchDrawer.hasAttribute('open')) {
        searchDrawer.open = false;
      }
    }
  });

  document.addEventListener('openable-element:close', (event) => {
    if (event.target && event.target.id === 'mini-cart') {
      document.documentElement.classList.remove(OPEN_CLASS);
      storage.set(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = document.getElementById('mini-cart');

    if (event.key === 'Escape' && drawer && drawer.hasAttribute('open')) {
      drawer.open = false;
    }
  });

  const setup = () => {
    const drawer = document.getElementById('mini-cart');
    const html = document.documentElement;

    if (!drawer) {
      return;
    }

    // The theme creates its focus trap lazily through this property: a no-op keeps the page usable
    // while the drawer is open (the trap would close it on the first click outside).
    drawer._focusTrap = { activate() {}, deactivate() {} };

    if (!html.classList.contains(RESTORING_CLASS)) {
      return;
    }

    if (!drawer.querySelector('[data-cart-item]')) {
      storage.set(false);
      html.classList.remove(OPEN_CLASS, RESTORING_CLASS);
      return;
    }

    // The theme animates the footer in on every open (unless reduced motion is preferred): a
    // restored drawer must look untouched, so that check is answered "yes" while it opens. The
    // header toggle re-applies the attribute asynchronously on the open event, hence the delay.
    const mediaFeatures = window.MediaFeatures;
    const prefersReducedMotion = mediaFeatures && mediaFeatures.prefersReducedMotion;

    if (prefersReducedMotion) {
      mediaFeatures.prefersReducedMotion = () => true;
      setTimeout(() => {
        mediaFeatures.prefersReducedMotion = prefersReducedMotion;
      }, 100);
    }

    drawer.setAttribute('open', '');

    // Flush styles now, while transitions are off, so the open state (pushed page, visible drawer)
    // is the starting point and nothing animates when transitions come back. Without this a
    // background tab may defer the first style pass until after the class is released.
    void document.body.offsetWidth;

    // Transitions come back once the open state has been painted (two frames), with a timer as a
    // fallback for background tabs, where animation frames do not run.
    const release = () => html.classList.remove(RESTORING_CLASS);
    const timer = setTimeout(release, 300);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clearTimeout(timer);
        release();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
